import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, SearchInput, Switch, TableSkeleton,
} from '../components/ui';

/* ── Routers ─────────────────────────────────────────────────────────────────
 * A router is an endpoint that picks the model for each request among the models listed
 * here, reading the description and usage tips of each together with the conversation.
 * A tier can use one in any slot in place of a model; proxy-llm then charges what the
 * router says the chosen model cost (backend/api/routes/llm_routers.py).
 *
 * The router's catalogue is read server-side with the stored key (POST
 * /api/llm-routers/catalogue), so the key never reaches the browser after it is saved. */

const DEFAULT_LIMITS = { min_candidates: 2, max_candidates: 16, max_chars: 2000 };
const DEFAULT_TEST_PROMPT = 'Say hello in one sentence.';
const EMPTY_ROW = { id: '', description: '', usage_tips: '' };
const EMPTY_FORM = {
  router_key: '', ui_name: '', base_url: '', api_key: '', active: true, sort_order: 0,
  rows: [EMPTY_ROW, EMPTY_ROW],
};

const DASH = <span className="text-faint">—</span>;

const formatTokens = (n) => {
  if (!n) return null;
  if (n >= 1000000) return `${+(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
};

// The router states prices and costs in USD, so they are shown as it states them.
const USD = (v, digits = 2) => (v == null ? null : `$${Number(v).toFixed(digits)}`);
const urlParts = (url) => {
  const m = /^https?:\/\/([^/]+)(\/.*)?$/.exec(url || '');
  return m ? { host: m[1], path: m[2] || '/' } : { host: url || '', path: '' };
};

const routerMatches = (r, q) => [r.ui_name, r.router_key, r.base_url,
  ...(r.candidates || []).map((c) => `${c.id} ${c.display_name || ''}`)]
  .some((v) => v && String(v).toLowerCase().includes(q));

/* ── What the catalogue says about one model, in one line ────────────────── */
function ModelFacts({ model }) {
  const p = model.prices || {};
  const facts = [
    model.vendor,
    model.context_length && `${formatTokens(model.context_length)} context`,
    model.max_output_tokens && `${formatTokens(model.max_output_tokens)} max output`,
    model.multimodal && `reads ${model.multimodal}`,
    p.input != null && p.output != null && `${USD(p.input)} in, ${USD(p.output)} out per 1M`,
  ].filter(Boolean);
  return (
    <div className="row-wrap gap-2 text-sm text-muted">
      <span>{facts.join(' · ')}</span>
      {model.default
        ? <Badge tone="ok">Router default</Badge>
        : <Badge tone="outline">Used only when listed</Badge>}
      {(model.specializations || []).map((s) => <Badge key={s} tone="neutral">{s}</Badge>)}
    </div>
  );
}

/* ── One candidate row in the editor ─────────────────────────────────────── */
function CandidateRow({ index, row, models, picked, limits, canRemove, onPick, onText, onRemove }) {
  const model = models.find((m) => m.id === row.id);
  const idBase = `router-row-${index}`;
  const counter = (v) => <span className="text-xs text-muted num">{(v || '').length}/{limits.max_chars}</span>;
  const textField = (field, label, hint, rows) => {
    const value = row[field] || '';
    const fallback = model ? (model[field] || '') : '';
    return (
      <Field label={label} htmlFor={`${idBase}-${field}`} hint={hint}
             error={value.length > limits.max_chars ? `At most ${limits.max_chars} characters.` : null}
             extra={(
               <span className="row gap-2" style={{ marginLeft: 'auto' }}>
                 {model && value !== fallback && (
                   <button type="button" className="btn btn-ghost btn-sm" onClick={() => onText(field, fallback)}>
                     Use the default
                   </button>
                 )}
                 {counter(value)}
               </span>
             )}>
        <textarea id={`${idBase}-${field}`} className="form-input" rows={rows} value={value}
                  disabled={!row.id} onChange={(e) => onText(field, e.target.value)} />
      </Field>
    );
  };
  return (
    <div className="card-inset">
      <div className="row between mb-3">
        <h3 className="row gap-2 text-base fw-600">
          <Badge tone="accent" square>{index + 1}</Badge>
          {model ? model.display_name : 'Choose a model'}
        </h3>
        <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={onRemove}
                disabled={!canRemove} aria-label={`Remove model ${index + 1}`} title="Remove">
          <Icon name="trash" />
        </button>
      </div>
      <Field label="Model" htmlFor={`${idBase}-model`} required className="mb-2">
        <select id={`${idBase}-model`} className="form-input" value={row.id}
                onChange={(e) => onPick(e.target.value)}>
          <option value="">Choose a model…</option>
          {/* A model already in another row is offered but disabled, so it cannot be picked twice. */}
          {models.map((m) => (
            <option key={m.id} value={m.id} disabled={picked.has(m.id) && m.id !== row.id}>
              {m.display_name}{m.id !== m.display_name ? ` (${m.id})` : ''}
            </option>
          ))}
          {row.id && !model && <option value={row.id}>{row.id} (not in the catalogue)</option>}
        </select>
      </Field>
      {model && <div className="mb-3"><ModelFacts model={model} /></div>}
      {textField('description', 'Description',
        'What the model is good at, and what it should not be used for.', 4)}
      {textField('usage_tips', 'Usage tips', 'When the router should pick it.', 2)}
    </div>
  );
}

/* ── Test result ─────────────────────────────────────────────────────────── */
function TestResult({ result: r }) {
  const ok = !!r.success;
  const raw = r.raw_response !== undefined ? r.raw_response : r;
  return (
    <div className="stack gap-3">
      <div className={`notice ${ok ? 'notice-ok' : 'notice-danger'} mb-0`}>
        <Icon name={ok ? 'checkCircle' : 'xCircle'} />
        <div className="notice-body">
          <div className="row-wrap gap-3">
            <span className="notice-title">{ok ? `Served by ${r.served_model || 'an unnamed model'}` : 'Failed'}</span>
            {r.status_code != null && <span className="num">HTTP {r.status_code}</span>}
            {r.elapsed_seconds != null && <span className="num">{r.elapsed_seconds}s</span>}
            {ok && <span className="num">{r.cost_usd != null ? `${USD(r.cost_usd, 6)}` : 'no cost stated'}</span>}
            {r.input_tokens != null && (
              <span className="num">
                {r.input_tokens} in{r.cached_tokens ? ` (${r.cached_tokens} cached)` : ''} / {r.output_tokens} out
              </span>
            )}
          </div>
          {r.detail && <div className="mt-1">{typeof r.detail === 'string' ? r.detail : JSON.stringify(r.detail)}</div>}
        </div>
      </div>
      {r.text && <div className="card-inset pre-wrap">{r.text}</div>}
      {r.headers && Object.keys(r.headers).length > 0 && (
        <dl className="kv">
          {Object.entries(r.headers).map(([k, v]) => (
            <React.Fragment key={k}><dt className="mono text-sm">{k}</dt><dd className="mono text-sm">{v}</dd></React.Fragment>
          ))}
        </dl>
      )}
      <details>
        <summary className="text-sm text-muted">Raw response</summary>
        <pre className="code-block mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/* ── The page ────────────────────────────────────────────────────────────── */
function Routers() {
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [catalogue, setCatalogue] = useState({ status: 'idle', models: [], limits: DEFAULT_LIMITS });
  const [testKey, setTestKey] = useState(null);
  const [testStates, setTestStates] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  const reload = async () => {
    try {
      const resp = await apiFetch('/api/llm-routers');
      if (resp.ok) { setRouters(await resp.json()); setLoadError(false); } else setLoadError(true);
    } catch (e) { setLoadError(true); }
  };

  useEffect(() => {
    (async () => { await reload(); setLoading(false); })();
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  // A new address or key may be another router: its models have to be read again.
  const updateEndpoint = (key, value) => {
    update(key, value);
    setCatalogue((c) => (c.status === 'ready' ? { ...c, status: 'stale' } : c));
  };

  const loadCatalogue = async (body) => {
    setCatalogue((c) => ({ ...c, status: 'loading', error: null }));
    try {
      const resp = await apiFetch('/api/llm-routers/catalogue', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setCatalogue((c) => ({ ...c, status: 'error', error: data.detail || 'The models did not load.' }));
        return null;
      }
      setCatalogue({ status: 'ready', router: data.router, models: data.models || [],
                     limits: { ...DEFAULT_LIMITS, ...(data.limits || {}) } });
      return data;
    } catch (e) {
      setCatalogue((c) => ({ ...c, status: 'error', error: 'The models did not load.' }));
      return null;
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingKey(null);
    setCatalogue({ status: 'idle', models: [], limits: DEFAULT_LIMITS });
    setShowForm(true);
  };

  const handleEdit = (r) => {
    setForm({
      router_key: r.router_key, ui_name: r.ui_name, base_url: r.base_url, api_key: '',
      active: !!r.active, sort_order: r.sort_order || 0,
      rows: (r.candidates || []).map((c) => ({ id: c.id, description: c.description || '', usage_tips: c.usage_tips || '' })),
      _api_key_set: !!r.api_key_set, _api_key_display: r.api_key_display || '',
    });
    setEditingKey(r.router_key);
    setShowForm(true);
    // The saved key reads the catalogue; the browser never holds it.
    loadCatalogue({ router_key: r.router_key });
  };

  const closeForm = () => { if (!saving) { setShowForm(false); setEditingKey(null); } };

  const fetchModels = () => {
    if (!form.base_url.trim()) { toast.error('Enter the router address first'); return; }
    if (!editingKey && !form.api_key.trim()) { toast.error('Enter the API key first'); return; }
    loadCatalogue(editingKey
      ? { router_key: editingKey, base_url: form.base_url.trim(), api_key: form.api_key.trim() || undefined }
      : { base_url: form.base_url.trim(), api_key: form.api_key.trim() });
  };

  // Picking a model fills in the router's own texts for it, ready to edit.
  const pickModel = (i, id) => setForm((f) => {
    const model = catalogue.models.find((m) => m.id === id);
    const rows = f.rows.map((row, j) => (j !== i ? row : {
      id, description: model ? model.description || '' : '', usage_tips: model ? model.usage_tips || '' : '',
    }));
    return { ...f, rows };
  });
  const setText = (i, field, value) => setForm((f) => ({
    ...f, rows: f.rows.map((row, j) => (j === i ? { ...row, [field]: value } : row)),
  }));
  const addRow = () => setForm((f) => ({ ...f, rows: [...f.rows, EMPTY_ROW] }));
  const removeRow = (i) => setForm((f) => ({ ...f, rows: f.rows.filter((_, j) => j !== i) }));

  const limits = catalogue.limits || DEFAULT_LIMITS;
  const picked = new Set(form.rows.map((r) => r.id).filter(Boolean));
  const canAdd = form.rows.length < limits.max_candidates && picked.size < catalogue.models.length
    && form.rows.every((r) => r.id);

  const problems = () => {
    if (!form.ui_name.trim()) return 'The display name is required';
    if (!editingKey && !form.router_key.trim()) return 'The router key is required';
    if (!form.base_url.trim()) return 'The router address is required';
    if (!editingKey && !form.api_key.trim()) return 'The API key is required';
    if (catalogue.status !== 'ready' && catalogue.status !== 'stale') return 'Load the router\'s models first';
    if (form.rows.some((r) => !r.id)) return 'Choose a model in every row, or remove the empty ones';
    if (form.rows.length < limits.min_candidates) return `Choose at least ${limits.min_candidates} models`;
    if (form.rows.some((r) => (r.description || '').length > limits.max_chars
      || (r.usage_tips || '').length > limits.max_chars)) return `Texts are limited to ${limits.max_chars} characters`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = problems();
    if (problem) { toast.error(problem); return; }
    const candidates = form.rows.map(({ id, description, usage_tips }) => ({ id, description, usage_tips }));
    const payload = editingKey
      ? { ui_name: form.ui_name.trim(), base_url: form.base_url.trim(), candidates,
          active: form.active, sort_order: parseInt(form.sort_order, 10) || 0 }
      : { router_key: form.router_key.trim(), ui_name: form.ui_name.trim(), base_url: form.base_url.trim(),
          api_key: form.api_key.trim(), candidates, active: form.active,
          sort_order: parseInt(form.sort_order, 10) || 0 };
    if (editingKey && form.api_key.trim()) payload.api_key = form.api_key.trim();
    setSaving(true);
    try {
      const resp = await apiFetch(editingKey ? `/api/llm-routers/${editingKey}` : '/api/llm-routers', {
        method: editingKey ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        toast.success(editingKey ? 'Router updated' : 'Router added');
        setShowForm(false);
        setEditingKey(null);
        reload();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to save the router');
      }
    } catch { toast.error('Failed to save the router'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!await confirm({
      title: `Delete router ${r.ui_name}?`,
      body: <p>Its key and model list are removed. A router a tier still uses cannot be deleted.</p>,
      confirmLabel: 'Delete router',
    })) return;
    try {
      const resp = await apiFetch(`/api/llm-routers/${r.router_key}`, { method: 'DELETE' });
      if (resp.ok) { toast.success('Router deleted'); reload(); }
      else { const err = await resp.json().catch(() => ({})); toast.error(err.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  /* ── Test ─────────────────────────────────────────────────────────────── */
  const testState = (key) => testStates[key] || { prompt: DEFAULT_TEST_PROMPT, loading: false, result: null };
  const setTestPrompt = (key, prompt) => setTestStates((s) => ({ ...s, [key]: { ...testState(key), prompt } }));
  const runTest = async (key) => {
    const state = testState(key);
    setTestStates((s) => ({ ...s, [key]: { ...state, loading: true, result: null } }));
    try {
      const resp = await apiFetch(`/api/llm-routers/${key}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: state.prompt }),
      });
      const data = await resp.json();
      setTestStates((s) => ({ ...s, [key]: { ...s[key], loading: false, result: data } }));
    } catch (err) {
      setTestStates((s) => ({ ...s, [key]: { ...s[key], loading: false,
        result: { success: false, detail: err.message } } }));
    }
  };

  const q = search.trim().toLowerCase();
  const visible = routers.filter((r) => !q || routerMatches(r, q));
  const testRouter = testKey ? routers.find((r) => r.router_key === testKey) : null;
  const addButton = (
    <button type="button" className="btn btn-primary" onClick={openCreate}>
      <Icon name="plus" /> Add router
    </button>
  );
  const catalogueNote = {
    idle: 'Enter the address and key, then load the models this router can pick.',
    loading: 'Reading the router\'s models…',
    stale: 'The address or key changed: load the models again before saving.',
    ready: `${catalogue.models.length} models available${catalogue.router ? ` on router ${catalogue.router}` : ''}.`,
  }[catalogue.status];

  return (
    <div>
      <PageHeader
        description="Endpoints that pick the model for each request among the models you list. A tier can use one in place of a model."
        actions={addButton}
      >
        <h1 className="page-title">
          Routers
          {!loading && <Badge tone="neutral" size="lg">{routers.length}</Badge>}
        </h1>
      </PageHeader>

      <div className="card card-flush">
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, key, address, model…"
                       label="Search routers" />
        </div>
        {loading ? (
          <TableSkeleton rows={3} cols={7} label="Loading routers" />
        ) : routers.length === 0 && loadError ? (
          <EmptyState icon="alertCircle" title="Could not load routers"
                      action={<button type="button" className="btn btn-secondary" onClick={reload}>Retry</button>}>
            The router list did not load. Nothing has been changed.
          </EmptyState>
        ) : routers.length === 0 ? (
          <EmptyState icon="routers" title="No routers yet" action={addButton}>
            A router picks the model for each request among the ones you list. Add one, then choose
            it for a tier.
          </EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState icon="search" title={`No routers match “${search.trim()}”`}
                      action={<button type="button" className="btn btn-secondary" onClick={() => setSearch('')}>Clear search</button>}>
            Search covers name, key, address and the listed models.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Router</th>
                  <th>Address</th>
                  <th>Models</th>
                  <th className="num">Context</th>
                  <th>Used by</th>
                  <th>Status</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const test = testState(r.router_key);
                  const names = (r.candidates || []).map((c) => c.display_name || c.id);
                  return (
                    <tr key={r.router_key}>
                      <td data-label="Router">
                        <div style={{ maxWidth: 220, minWidth: 0 }}>
                          <span className="cell-primary">{r.ui_name}</span>
                          <span className="cell-sub mono truncate" title={r.router_key}>{r.router_key}</span>
                        </div>
                      </td>
                      <td data-label="Address">
                        {/* Host and path on two lines: the path names the router (/r/jev/v1),
                            and truncating one long line cut exactly that part off. */}
                        <div style={{ maxWidth: 260, minWidth: 0 }} title={r.base_url}>
                          <div className="mono text-sm truncate">{urlParts(r.base_url).host}</div>
                          <span className="cell-sub mono truncate">{urlParts(r.base_url).path}</span>
                        </div>
                      </td>
                      <td data-label="Models" className="wrap">
                        <span className="text-sm">{names.slice(0, 3).join(', ')}</span>
                        {names.length > 3 && <span className="text-sm text-muted"> and {names.length - 3} more</span>}
                      </td>
                      <td data-label="Context" className="num">{formatTokens(r.context_window) || DASH}</td>
                      <td data-label="Used by" className="wrap">
                        {r.used_by?.length ? <span className="text-sm">{r.used_by.join(', ')}</span>
                          : <span className="text-faint text-sm">No tier</span>}
                      </td>
                      <td data-label="Status">
                        <div className="status-lines">
                          <span className={r.active ? 'text-ok' : 'text-muted'}>
                            <span className="dot" aria-hidden="true" />{r.active ? 'Active' : 'Inactive'}
                          </span>
                          {!r.api_key_set && (
                            <span className="text-warn"><span className="dot" aria-hidden="true" />No API key</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setTestKey(r.router_key)}
                                  aria-label={`Test ${r.ui_name}`}>
                            {test.loading ? <span className="spinner-sm" /> : <Icon name="play" />} Test
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEdit(r)}
                                  aria-label={`Edit ${r.ui_name}`} title="Edit">
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn-danger-soft btn-sm btn-icon" onClick={() => handleDelete(r)}
                                  aria-label={`Delete ${r.ui_name}`} title="Delete router">
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={showForm} onClose={closeForm} maxWidth={880}
             title={editingKey ? `Edit ${form.ui_name || editingKey}` : 'Add router'}
             description="The router reads each model's texts with the conversation to choose one.">
        <form onSubmit={handleSubmit}>
          <section aria-labelledby="router-form-identity">
            <h2 id="router-form-identity" className="section-title">Identity</h2>
            <div className="form-row">
              <Field label="Display name" htmlFor="router-ui-name" required>
                <input id="router-ui-name" className="form-input" value={form.ui_name}
                       onChange={(e) => update('ui_name', e.target.value)} placeholder="Router jev" />
              </Field>
              <Field label="Router key" htmlFor="router-key" required
                     hint={editingKey ? 'Tiers refer to the router by this key, so it cannot change.'
                       : 'Lowercase, spaces become dashes. Tiers refer to the router by it.'}>
                <input id="router-key" className="form-input mono" value={form.router_key} disabled={!!editingKey}
                       spellCheck={false} autoComplete="off" placeholder="jev"
                       onChange={(e) => update('router_key', e.target.value.toLowerCase().replace(/\s/g, '-'))} />
              </Field>
            </div>
          </section>

          <section aria-labelledby="router-form-endpoint" className="mt-4">
            <h2 id="router-form-endpoint" className="section-title">Address &amp; key</h2>
            <div className="form-row">
              <Field label="Router address" htmlFor="router-base-url" required
                     hint="The router's API root, ending in /v1.">
                <input id="router-base-url" className="form-input mono" value={form.base_url} inputMode="url"
                       spellCheck={false} autoComplete="off" placeholder="https://host/r/jev/v1"
                       onChange={(e) => updateEndpoint('base_url', e.target.value)} />
              </Field>
              <Field label="API key" htmlFor="router-api-key" required={!editingKey}
                     hint={editingKey && form._api_key_set
                       ? <>Current key <code>{form._api_key_display}</code>. Leave blank to keep it.</>
                       : 'Write-only: after saving, only a masked preview is shown.'}>
                <input id="router-api-key" className="form-input" type="password" autoComplete="new-password"
                       spellCheck={false} value={form.api_key}
                       placeholder={editingKey ? 'Leave blank to keep the current key' : 'Enter the API key…'}
                       onChange={(e) => updateEndpoint('api_key', e.target.value)} />
              </Field>
            </div>
            <div className="row-wrap gap-3">
              <button type="button" className="btn btn-secondary" onClick={fetchModels}
                      disabled={catalogue.status === 'loading'}>
                {catalogue.status === 'loading' ? <span className="spinner-sm" /> : <Icon name="refresh" />}
                {catalogue.status === 'ready' ? 'Reload models' : 'Load models'}
              </button>
              <span className={`text-sm ${catalogue.status === 'stale' ? 'text-warn' : 'text-muted'}`} aria-live="polite">
                {catalogueNote}
              </span>
            </div>
            {catalogue.status === 'error' && (
              <div className="notice notice-danger mt-3 mb-0" role="alert">
                <Icon name="alertCircle" />
                <div className="notice-body">{catalogue.error}</div>
              </div>
            )}
          </section>

          <section aria-labelledby="router-form-models" className="mt-4">
            <h2 id="router-form-models" className="section-title">
              Models <span className="text-sm text-muted fw-400">
                {limits.min_candidates} to {limits.max_candidates}, in the order the router reads them
              </span>
            </h2>
            {catalogue.status === 'idle' && !editingKey ? (
              <p className="text-sm text-muted">Load the models to choose among them.</p>
            ) : (
              <div className="stack gap-3">
                {form.rows.map((row, i) => (
                  <CandidateRow
                    key={i} index={i} row={row} models={catalogue.models} picked={picked} limits={limits}
                    canRemove={form.rows.length > limits.min_candidates}
                    onPick={(id) => pickModel(i, id)} onText={(field, v) => setText(i, field, v)}
                    onRemove={() => removeRow(i)}
                  />
                ))}
                <div>
                  <button type="button" className="btn btn-secondary" onClick={addRow} disabled={!canAdd}>
                    <Icon name="plus" /> Add model
                  </button>
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="router-form-availability" className="mt-4">
            <h2 id="router-form-availability" className="section-title">Availability</h2>
            <Switch checked={form.active} onChange={(v) => update('active', v)} label="Active"
                    description="Inactive routers are left out of the tier pickers, and tiers that use one stop being served." />
          </section>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner-sm" /> Saving…</> : (editingKey ? 'Save router' : 'Add router')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={!!testRouter} onClose={() => setTestKey(null)} maxWidth={640}
             title={testRouter ? `Test ${testRouter.ui_name}` : ''}
             description="Sends one prompt through the router with its saved models and shows which one answered.">
        {testRouter && (
          <>
            <dl className="kv mb-4">
              <dt>Address</dt><dd className="mono">{testRouter.base_url}</dd>
              <dt>Models</dt><dd>{(testRouter.candidates || []).map((c) => c.display_name || c.id).join(', ')}</dd>
            </dl>
            <form onSubmit={(e) => { e.preventDefault(); if (!testState(testKey).loading) runTest(testKey); }}>
              <label className="form-label" htmlFor="router-test-prompt">Prompt</label>
              <div className="input-group">
                <input id="router-test-prompt" className="form-input" value={testState(testKey).prompt}
                       onChange={(e) => setTestPrompt(testKey, e.target.value)} placeholder="Test prompt…" />
                <button type="submit" className="btn btn-primary" disabled={testState(testKey).loading}>
                  {testState(testKey).loading ? <><span className="spinner-sm" /> Testing…</> : <><Icon name="play" /> Run test</>}
                </button>
              </div>
            </form>
            <div className="mt-4" aria-live="polite">
              {testState(testKey).result && <TestResult result={testState(testKey).result} />}
            </div>
          </>
        )}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setTestKey(null)}>Close</button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default Routers;
